import React, {useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
  Popover,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';

import {useDismissDiscrepancyMutation as useDismissDiscrepancyMutationV2} from '../../data/copilotV2Api';
import {trackEvent} from '../../util';
import {BLACK_100, GREEN_95} from '../../theme';

const styles = {
  actionButton: {
    borderRadius: '100%',
    border: '1px solid #D3D6D4',
    padding: '6px',
  },
  dismiss: {
    header: {
      color: BLACK_100,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: '1.25rem',
      whiteSpace: 'nowrap',
    },
    formControlLabel: {
      display: 'flex',
      padding: '8px 12px',
      alignItems: 'center',
      gap: '8px',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #DDE0DE',
      background: '#FFF',

      '&:has(.Mui-checked)': {
        backgroundColor: '#F2F5F4',
      },

      '.MuiFormControlLabel-label': {
        color: BLACK_100,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '1.25rem',
        whiteSpace: 'nowrap',
      },
      '.MuiRadio-root': {
        padding: 0,
      },
    },
    textField: {
      '.MuiOutlinedInput-root': {
        height: '2rem',
        borderRadius: '0.25rem',
        border: '1px solid #DDE0DE',
        paddingY: 0,
        '&.Mui-focused fieldset': {
          borderColor: GREEN_95,
        },
      },
    },
  },
  overlay: {
    noReason: {
      color: '#819190',
      fontSize: '12px',
      border: '1px solid #DDE0DE',
      borderRadius: '8px',
      '&:hover': {
        backgroundColor: '#F2F5F4',
      },
    },
    button: {
      fontSize: '12px',
      padding: '6px 16px',
      borderRadius: '8px',
    },
  },
};

const DEFAULT_DISMISS_REASON = 'no_reason';

export const FOOTING_DISMISS_REASONS = [
  {value: 'incorrect_formula', label: 'Incorrect formula'},
  {value: 'incorrect_formula_inputs', label: 'Incorrect inputs in formula'},
];

export const INCONSISTENCY_DISMISS_REASONS = [
  {value: 'items_not_related', label: 'Items not related'},
  {value: 'different_periods', label: 'Items are for different periods'},
];

const DismissSuggestionButton = ({
  documentId,
  discrepancyId,
  checkType,
  reasons,
  headerText,
  errorMessage,
  popoverId,
  disabled = false,
  onDismiss,
  onDismissError,
}) => {
  const dismissAnchorRef = useRef(null);
  const [showDismissal, setShowDismissal] = useState(false);
  const [dismissReason, setDismissReason] = useState(DEFAULT_DISMISS_REASON);
  const [dismissOtherDetail, setDismissOtherDetail] = useState('');
  const [isProcessingDismissal, setProcessingDismissal] = useState(false);
  const [dismissDiscrepancyV2] = useDismissDiscrepancyMutationV2();

  const handleDismiss = async (dismissal = {}) => {
    const resolvedDismissReason = dismissal.reason ?? dismissReason;
    const resolvedDismissOtherDetail =
      dismissal.otherReason ?? dismissOtherDetail;
    const dismissStartTimestamp = Date.now();
    setProcessingDismissal(true);

    const data = {
      document_id: documentId,
      is_pdf: true,
      dismissed_cards: {
        footings: [],
        inconsistencies: [],
      },
    };
    data.dismissed_cards[checkType] = [
      {
        id: discrepancyId,
        reason: resolvedDismissReason,
        other_reason: resolvedDismissOtherDetail,
      },
    ];

    try {
      const result = await dismissDiscrepancyV2({data});

      if (_.has(result, 'error')) {
        throw new Error(result.error.status);
      }

      trackEvent(
        'Dismissed Discrepancy (PDF Review) Completed',
        {
          discrepancyId,
          reason: resolvedDismissReason,
          otherReason: resolvedDismissOtherDetail,
        },
        Date.now() - dismissStartTimestamp
      );

      setDismissReason(DEFAULT_DISMISS_REASON);
      setDismissOtherDetail('');
      setShowDismissal(false);
      onDismiss?.(discrepancyId);
    } catch (error) {
      trackEvent(
        'Dismissed Discrepancy (PDF Review) Failed',
        {
          discrepancyId,
          reason: resolvedDismissReason,
          otherReason: resolvedDismissOtherDetail,
          error: error.message,
        },
        Date.now() - dismissStartTimestamp
      );
      console.error('Error dismissing discrepancy', error);
      onDismissError?.(errorMessage);
    } finally {
      setProcessingDismissal(false);
    }
  };

  const handleDismissReasonChange = (event) => {
    setDismissReason(event.target.value);
    setDismissOtherDetail('');
  };

  return (
    <>
      <Tooltip
        title="Dismiss suggestion"
        placement="top"
        enterDelay={500}
        open={showDismissal ? false : undefined}
      >
        <IconButton
          ref={dismissAnchorRef}
          aria-label="Dismiss suggestion"
          aria-controls={showDismissal ? popoverId : undefined}
          aria-expanded={showDismissal}
          variant="outlined"
          disabled={disabled || isProcessingDismissal}
          sx={styles.actionButton}
          onClick={() => setShowDismissal(true)}
        >
          <CloseIcon sx={{width: '1rem', height: '1rem', color: '#0F1E24'}} />
        </IconButton>
      </Tooltip>
      <Popover
        id={popoverId}
        open={showDismissal}
        anchorEl={dismissAnchorRef.current}
        onClose={() => setShowDismissal(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 'max-content',
              width: 'max-content',
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              mt: 0.5,
            },
          },
        }}
      >
        <Stack direction="column" spacing="0.75rem">
          <Stack direction="column" spacing="1rem" padding="1rem">
            <Typography style={styles.dismiss.header}>{headerText}</Typography>
            <RadioGroup
              name="dismiss-buttons-group"
              value={dismissReason}
              onChange={handleDismissReasonChange}
            >
              <Stack direction={'column'} spacing={'1rem'}>
                {reasons.map(({value, label}) => (
                  <FormControlLabel
                    key={value}
                    sx={styles.dismiss.formControlLabel}
                    value={value}
                    control={<Radio size="small" />}
                    label={label}
                    disabled={isProcessingDismissal}
                  />
                ))}

                <Stack direction="column" spacing="0.375rem">
                  <FormControlLabel
                    sx={styles.dismiss.formControlLabel}
                    value="other"
                    control={<Radio size="small" />}
                    label="Other reason"
                    disabled={isProcessingDismissal}
                  />
                  {dismissReason === 'other' && (
                    <TextField
                      disabled={isProcessingDismissal}
                      sx={styles.dismiss.textField}
                      variant="outlined"
                      fullWidth={true}
                      value={dismissOtherDetail}
                      onChange={(event) =>
                        setDismissOtherDetail(event.target.value)
                      }
                      placeholder="Share details (optional) ..."
                    />
                  )}
                </Stack>
              </Stack>
            </RadioGroup>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} px={2} pb={2} pt={1.5}>
          <LoadingButton
            variant="contained"
            color="green"
            fullWidth={false}
            style={styles.overlay.button}
            onClick={() => handleDismiss()}
            loading={isProcessingDismissal}
          >
            Dismiss Issue
          </LoadingButton>
          <Button
            variant="text"
            disabled={isProcessingDismissal}
            sx={styles.overlay.noReason}
            onClick={() => {
              setDismissReason(DEFAULT_DISMISS_REASON);
              setDismissOtherDetail('');
              handleDismiss({
                reason: DEFAULT_DISMISS_REASON,
                otherReason: '',
              });
            }}
          >
            Dismiss without reason
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

DismissSuggestionButton.propTypes = {
  documentId: PropTypes.string,
  discrepancyId: PropTypes.string.isRequired,
  checkType: PropTypes.oneOf(['footings', 'inconsistencies']).isRequired,
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  headerText: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  popoverId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onDismiss: PropTypes.func,
  onDismissError: PropTypes.func,
};

export default DismissSuggestionButton;
